(() => {
  'use strict';

  function init(root) {
    if (!root || root.dataset.initialized) return;
    root.dataset.initialized = 'true';
    const sectionId = root.dataset.sectionId;
    const messages = JSON.parse(document.getElementById(`sensme-cart-messages-${sectionId}`).textContent);
    const shopRoot = window.Shopify?.routes?.root || '/';
    let busy = false;
    let destroyAutocomplete = () => {};
    let restoreBottomUi = () => {};
    let appliedCode = '';
    const ordersApi = 'https://sensme-orders.sensme-manager.workers.dev';
    const attemptKey = 'sensme-order-attempt-v1';
    let orderBusy = false;
    let retryTimer, healthTimer;
    let automaticChecks = 0;
    let attempt = null;
    let storageAvailable = true;
    try { attempt = JSON.parse(sessionStorage.getItem(attemptKey) || 'null'); }
    catch { storageAvailable = false; }
    const abort = new AbortController();
    const on = (target, event, handler) => target?.addEventListener(event, handler, { signal: abort.signal });
    on(document, 'shopify:section:unload', event => {
      if (event.target.contains(root)) {
        clearTimeout(retryTimer);
        clearTimeout(healthTimer);
        abort.abort();
      }
    });

    async function fetchJSON(url, options = {}, timeoutMs = 12000) {
      const controller = new AbortController();
      const cancel = () => controller.abort();
      if (abort.signal.aborted) cancel();
      else abort.signal.addEventListener('abort', cancel, {once: true});
      const timeout = setTimeout(cancel, timeoutMs);
      try {
        const response = await fetch(url, {...options, signal: controller.signal});
        return {response, data: await response.json()};
      } finally {
        clearTimeout(timeout);
        abort.signal.removeEventListener('abort', cancel);
      }
    }

    function status(message, error = false, promo = false) {
      let node = root.querySelector(promo ? '#promo-status' : '[data-cart-status]');
      if (!node) {
        node = document.createElement('p');
        node.dataset.cartStatus = '';
        node.className = 'custom-promo-status';
        node.setAttribute('role', 'status');
        root.querySelector('.custom-cart-unified-box, .custom-cart-container').append(node);
      }
      node.textContent = message;
      node.classList.toggle('err', error);
    }

    function setBusy(value) {
      busy = value;
      root.querySelector('.custom-cart-items-section')?.setAttribute('aria-busy', String(value));
      root.querySelectorAll('.custom-qty-btn, .custom-cart-item-remove, #promo-apply-btn').forEach(button => {
        button.disabled = value;
      });
      const submit = root.querySelector('.custom-checkout-submit');
      if (submit) submit.disabled = value || orderBusy || submit.dataset.ready !== 'true';
    }

    function syncPromo(cart) {
      appliedCode = (cart.discount_codes || []).find(code => code.applicable)?.code || '';
      const input = root.querySelector('#promo-code-input');
      const button = root.querySelector('#promo-apply-btn');
      if (input) {
        input.value = appliedCode;
        input.readOnly = !!appliedCode;
      }
      if (button) button.textContent = appliedCode ? 'Видалити' : 'Застосувати';
    }

    async function renderCart(cart) {
      let html = cart.sections?.[sectionId];
      if (!html) {
        const url = new URL(window.location.href);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { signal: abort.signal });
        if (!response.ok) throw new Error(messages.cartError);
        html = await response.text();
      }
      const documentCopy = new DOMParser().parseFromString(html, 'text/html');
      const next = documentCopy.querySelector('[data-sensme-cart]');
      if (!next) throw new Error(messages.cartError);
      const wasPromoOpen = !!root.querySelector('#promo-body.is-open');
      if (!cart.item_count) {
        destroyAutocomplete();
        restoreBottomUi();
        root.querySelector('.custom-cart-container').replaceWith(next.querySelector('.custom-cart-container'));
      } else {
        const currentItems = root.querySelector('.custom-cart-items-section');
        const nextItems = next.querySelector('.custom-cart-items-section');
        if (currentItems && nextItems) currentItems.replaceWith(nextItems);
        else {
          root.querySelector('.custom-cart-container').replaceWith(next.querySelector('.custom-cart-container'));
          initForm();
        }
        if (wasPromoOpen || appliedCode) togglePromo(true);
      }
      syncPromo(cart);
      document.querySelectorAll('.m-cart-count').forEach(counter => {
        counter.textContent = cart.item_count;
        counter.classList.toggle('m:hidden', !cart.item_count);
        counter.closest('a')?.setAttribute('aria-label', `Кошик: ${cart.item_count}`);
      });
      if (window.MinimogSettings) window.MinimogSettings.cartItemCount = cart.item_count;
    }

    async function updateCart(endpoint, payload) {
      const response = await fetch(`${shopRoot}cart/${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: [sectionId], sections_url: window.location.pathname + window.location.search }),
        signal: abort.signal,
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || messages.cartError);
      await renderCart(cart);
      return cart;
    }

    async function changeQuantity(button) {
      if (busy) return;
      const row = button.closest('.custom-cart-item');
      const input = row.querySelector('.custom-qty-input');
      const remove = button.matches('.custom-cart-item-remove');
      const quantity = remove ? 0 : Number(input.value) + (button.dataset.action === 'plus' ? 1 : -1);
      if (quantity < 0 || quantity > Number(input.max || 99)) return;
      const oldIndex = [...root.querySelectorAll('.custom-cart-item')].indexOf(row);
      const action = button.dataset.action;
      setBusy(true);
      try {
        await updateCart('change', { id: row.dataset.lineItem, quantity });
        const rows = root.querySelectorAll('.custom-cart-item');
        const target = rows[Math.min(oldIndex, rows.length - 1)];
        target?.querySelector(action ? `[data-action="${action}"]` : '.custom-cart-item-remove')?.focus();
      } catch (error) {
        if (error.name !== 'AbortError') {
          try {
            const response = await fetch(shopRoot + 'cart.js', {signal: abort.signal});
            if (!response.ok) throw new Error(messages.cartError);
            await renderCart(await response.json());
            status(error.message, true);
          } catch { status('Не вдалося оновити кошик. Оновіть сторінку перед оформленням.', true); }
        }
      } finally {
        setBusy(false);
      }
    }

    function togglePromo(open) {
      const body = root.querySelector('#promo-body');
      const button = root.querySelector('#promo-toggle');
      if (!body || !button) return;
      const expanded = open ?? !body.classList.contains('is-open');
      body.classList.toggle('is-open', expanded);
      button.classList.toggle('is-open', expanded);
      button.setAttribute('aria-expanded', String(expanded));
    }

    async function applyPromo() {
      if (busy) return;
      const input = root.querySelector('#promo-code-input');
      const removing = !!appliedCode;
      const code = removing ? '' : input.value.trim();
      if (!removing && !code) {
        status(messages.emptyCode, true, true);
        input.focus();
        return;
      }
      setBusy(true);
      status('Перевіряємо промокод…', false, true);
      try {
        const cart = await updateCart('update', { discount: code });
        const applied = (cart.discount_codes || []).some(entry => entry.applicable && entry.code.toLowerCase() === code.toLowerCase());
        togglePromo(true);
        if (removing) status(messages.codeRemoved, false, true);
        else status(applied ? `✓ Знижку −${new Intl.NumberFormat('uk-UA', {style: 'currency', currency: cart.currency || 'UAH'}).format(cart.total_discount / 100)} застосовано` : 'Промокод не застосовано. Перевірте написання, термін дії та умови для товарів у кошику.', !applied, true);
      } catch (error) {
        if (error.name !== 'AbortError') status('Не вдалося перевірити промокод. Перевірте з’єднання та спробуйте ще раз.', true, true);
      } finally {
        setBusy(false);
      }
    }

    function toggleDelivery(form) {
      const selected = form.querySelector('[data-control="delivery"] input:checked')?.id.replace('delivery-', '');
      form.querySelectorAll('.custom-delivery-fields').forEach(fields => {
        const active = fields.dataset.delivery === selected;
        fields.hidden = !active;
        fields.querySelectorAll('input').forEach(input => {
          input.disabled = !active;
          input.required = active && input.placeholder.includes('*');
        });
      });
      const patronymic = form.querySelector('#patronymic-field');
      if (patronymic) {
        const active = selected === 'courier';
        patronymic.hidden = !active;
        patronymic.querySelector('input').required = active;
        patronymic.querySelector('input').disabled = !active;
      }
    }

    function togglePayment(form) {
      const selected = form.querySelector('[data-control="payment"] input:checked')?.id.replace('payment-', '');
      form.querySelectorAll('.custom-payment-hint').forEach(hint => {
        hint.hidden = hint.dataset.payment !== selected;
      });
    }

    function initNovaPoshta(form) {
      const api = 'https://sensme-npapikey.sensme-manager.workers.dev/api/novaposhta/';
      const controls = [];
      const cache = new Map();
      function autocomplete(input, load, select, invalidate, kind) {
        const wrapper = input.closest('.custom-form-field');
        wrapper.classList.add('np-autocomplete-wrapper');
        const list = document.createElement('div');
        list.className = 'np-autocomplete-dropdown';
        list.id = `np-${sectionId}-${controls.length}`;
        list.setAttribute('role', 'listbox');
        document.body.append(list);
        input.autocomplete = 'off';
        input.setAttribute('autocorrect', 'off');
        input.setAttribute('autocapitalize', 'off');
        input.spellcheck = false;
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-controls', list.id);
        input.setAttribute('aria-expanded', 'false');
        let timer, blurTimer, pointer, controller, suppressClick = false, revision = 0, items = [], active = -1;
        function position() {
          if (!list.classList.contains('active')) return;
          if (!input.isConnected || input.disabled) return close();
          const rect = input.getBoundingClientRect();
          list.style.top = `${rect.bottom + window.scrollY + 4}px`;
          list.style.left = `${rect.left + window.scrollX}px`;
          list.style.width = `${rect.width}px`;
          list.style.right = 'auto';
        }
        function close() {
          clearTimeout(timer);
          clearTimeout(blurTimer);
          pointer = null;
          controller?.abort();
          revision++;
          list.classList.remove('active');
          input.setAttribute('aria-expanded', 'false');
          input.removeAttribute('aria-activedescendant');
        }
        function message(text, kind = 'empty') {
          items = [];
          active = -1;
          input.removeAttribute('aria-activedescendant');
          const node = document.createElement('div');
          node.className = `np-autocomplete-${kind}`;
          node.setAttribute('role', 'status');
          node.textContent = text;
          list.replaceChildren(node);
          list.classList.add('active');
          position();
          input.setAttribute('aria-expanded', 'true');
        }
        async function search(browse = false) {
          close();
          if (input.disabled || !input.isConnected) return;
          const current = revision;
          controller = new AbortController();
          const pending = controller;
          const timeout = setTimeout(() => pending.abort(), 12000);
          const loader = setTimeout(() => { if (current === revision) message('Завантаження…', 'loader'); }, 150);
          try {
            const query = browse && ['warehouses', 'postomats'].includes(kind) ? '' : input.value.trim();
            const result = await load(query, controller.signal);
            if (current !== revision || abort.signal.aborted) return;
            if (typeof result === 'string') return message(result);
            delete input.dataset.npFallback;
            input.setCustomValidity(input.value.trim() && !input.dataset.npRef && kind !== 'streets' ? 'Оберіть значення зі списку' : '');
            const queryNumber = input.value.trim().toLowerCase();
            const score = item => String(item.Number) === queryNumber ? 0 : String(item.Number).startsWith(queryNumber) ? 1 : 2;
            items = [...result].sort((a, b) => browse ? 0 : score(a) - score(b)).slice(0, 50);
            list.classList.add('active');
            input.setAttribute('aria-expanded', 'true');
            position();
            active = -1;
            if (!items.length) return message('Нічого не знайдено. Спробуйте інший запит.');
            list.replaceChildren(...items.map((item, index) => {
              const option = document.createElement('div');
              option.className = 'np-autocomplete-item';
              option.id = `${list.id}-${index}`;
              option.dataset.index = index;
              option.setAttribute('role', 'option');
              option.setAttribute('aria-selected', 'false');
              const title = document.createElement('div');
              title.className = 'np-item-title';
              title.textContent = kind === 'cities' ? (item.MainDescription || item.Description || item.Present) : item.Number ? (kind === 'postomats' ? 'Поштомат №' : 'Відділення №') + item.Number : item.Description;
              const subtitle = document.createElement('div');
              subtitle.className = 'np-item-subtitle';
              subtitle.textContent = kind === 'cities' ? [item.Region, item.AreaDescription || item.Area].filter(Boolean).join(', ') || item.Present || '' : item.Number ? item.Description : '';
              option.append(title);
              if (subtitle.textContent && subtitle.textContent !== title.textContent) option.append(subtitle);
              return option;
            }));
          } catch (error) {
            if (current === revision && !abort.signal.aborted) {
              input.dataset.npFallback = '1';
              input.setCustomValidity('');
              validateInput(input, false);
              message('Пошук Нової пошти тимчасово недоступний. Введіть повну адресу вручну — менеджер її перевірить.', 'error');
            }
          } finally {
            clearTimeout(timeout);
            clearTimeout(loader);
          }
        }
        function choose(index) {
          const item = items[index];
          if (!item) return;
          input.value = item.Present || item.Description;
          input.dataset.npRef = item.Ref;
          delete input.dataset.npFallback;
          input.scrollLeft = 0;
          input.setCustomValidity('');
          wrapper.classList.remove('has-error');
          input.setAttribute('aria-invalid', 'false');
          validateInput(input);
          close();
          select(item);
        }
        on(input, 'input', () => {
          close();
          delete input.dataset.npRef;
          input.setCustomValidity(kind === 'streets' || input.dataset.npFallback === '1' ? '' : 'Оберіть значення зі списку');
          invalidate();
          timer = setTimeout(search, 200);
        });
        on(input, 'focus', () => search(true));
        on(input, 'click', () => { if (!list.classList.contains('active')) void search(true); });
        function finishBlur() {
          close();
          if (kind !== 'streets' && input.value.trim() && !input.dataset.npRef && input.dataset.npFallback !== '1') {
            input.value = '';
            input.setCustomValidity('');
            invalidate();
            validateInput(input, false);
          }
        }
        on(input, 'blur', () => {
          clearTimeout(blurTimer);
          if (pointer) return;
          blurTimer = setTimeout(() => {
            if (document.activeElement !== input && !pointer) finishBlur();
          }, 250);
        });
        on(list, 'pointerdown', event => {
          if (event.button !== 0) return;
          suppressClick = true;
          clearTimeout(blurTimer);
          const option = event.target.closest('[data-index]');
          pointer = {id: event.pointerId, x: event.clientX, y: event.clientY, option, revision, moved: false};
          if (event.pointerType === 'mouse') event.preventDefault();
        });
        on(list, 'pointermove', event => {
          if (pointer?.id === event.pointerId && Math.hypot(event.clientX - pointer.x, event.clientY - pointer.y) > 10) pointer.moved = true;
        });
        on(list, 'pointerup', event => {
          const tap = pointer;
          pointer = null;
          if (!tap || tap.id !== event.pointerId || tap.moved || tap.revision !== revision) return;
          if (tap.option && tap.option === event.target.closest('[data-index]')) {
            event.preventDefault();
            choose(Number(tap.option.dataset.index));
          }
        });
        on(list, 'pointercancel', () => { pointer = null; });
        const outsidePointer = event => {
          if (event.target === input || list.contains(event.target)) return;
          if (document.activeElement !== input) finishBlur();
          else close();
        };
        on(document, 'pointerdown', outsidePointer);
        on(list, 'click', event => {
          if (suppressClick) { suppressClick = false; return; }
          if (!list.classList.contains('active')) return;
          const option = event.target.closest('[data-index]');
          if (option) choose(Number(option.dataset.index));
        });
        on(input, 'keydown', event => {
          if (event.key === 'Escape') return close();
          if (!list.classList.contains('active') || !items.length) return;
          if (event.key === 'Enter') {
            event.preventDefault();
            if (active >= 0) choose(active);
          }
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            active = (active + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
            [...list.children].forEach((option, index) => {
              option.classList.toggle('active', index === active);
              option.setAttribute('aria-selected', String(index === active));
            });
            input.setAttribute('aria-activedescendant', list.children[active].id);
            list.children[active].scrollIntoView({ block: 'nearest' });
          }
        });
        window.addEventListener('scroll', position, { capture: true, passive: true, signal: abort.signal });
        on(window, 'resize', position);
        on(window.visualViewport, 'resize', position);
        on(window.visualViewport, 'scroll', position);
        on(form, 'transitionend', position);
        const destroy = () => {
          close();
          list.remove();
          document.removeEventListener('pointerdown', outsidePointer);
          window.removeEventListener('scroll', position, true);
          window.removeEventListener('resize', position);
          window.visualViewport?.removeEventListener('resize', position);
          window.visualViewport?.removeEventListener('scroll', position);
          form.removeEventListener('transitionend', position);
        };
        abort.signal.addEventListener('abort', destroy, { once: true });
        const control = { close, destroy, reset() { close(); input.value = ''; delete input.dataset.npRef; delete input.dataset.npFallback; input.setCustomValidity(''); validateInput(input, false); invalidate(); } };
        controls.push(control);
        return control;
      }
      async function request(endpoint, payload, signal) {
        const key = endpoint + ':' + JSON.stringify(payload);
        const cached = cache.get(key);
        if (cached && Date.now() - cached.time < 600000) return cached.items;
        const response = await fetch(api + endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload), signal,
        });
        if (response.status === 429) throw new Error('rate-limit');
        if (!response.ok) throw new Error('Nova Poshta request failed');
        const data = await response.json();
        if (!data.success) throw new Error('Nova Poshta request failed');
        const items = data[endpoint] || [];
        if (cache.size >= 100) cache.delete(cache.keys().next().value);
        cache.set(key, {time: Date.now(), items});
        return items;
      }
      form.querySelectorAll('.custom-delivery-fields').forEach(fields => {
        const [city, destination] = fields.querySelectorAll('.custom-input');
        if (!city || !destination) return;
        let cityRef = '';
        const endpoint = { branch: 'warehouses', postomat: 'postomats', courier: 'streets' }[fields.dataset.delivery];
        const destinationControl = autocomplete(destination, (query, signal) => {
          if (!cityRef) {
            if (city.dataset.npFallback === '1') throw new Error('manual-city');
            return 'Спочатку оберіть місто зі списку';
          }
          if (endpoint === 'streets' && query.length < 2) return 'Введіть щонайменше 2 літери вулиці';
          return request(endpoint, { cityRef, query }, signal);
        }, () => {}, () => {}, endpoint);
        autocomplete(city, (query, signal) => query.length < 2 ? 'Почніть вводити назву міста' : request('cities', { query }, signal), item => {
          cityRef = item.Ref;
          destinationControl.reset();
          city.scrollLeft = 0;
        }, () => { cityRef = ''; destinationControl.reset(); }, 'cities');
      });
      on(form, 'change', event => {
        if (event.target.closest('[data-control="delivery"]')) {
          controls.forEach(control => control.reset());
          form.querySelectorAll('.custom-delivery-fields input, #customer-patronymic').forEach(input => {
            input.value = '';
            input.setCustomValidity('');
            validateInput(input, false);
          });
        }
      });
      let stopped = false;
      const warm = async () => {
        for (const query of ['Київ', 'Харків', 'Одеса', 'Дніпро', 'Львів']) {
          if (stopped || abort.signal.aborted) return;
          try {
            const cities = await request('cities', {query}, abort.signal);
            if (cities[0]?.Ref) await request('warehouses', {cityRef: cities[0].Ref, query: ''}, abort.signal);
          } catch { /* A failed warmup must not change field validation. */ }
        }
      };
      const warmTimer = setTimeout(() => { void warm(); }, 2000);
      abort.signal.addEventListener('abort', () => clearTimeout(warmTimer), {once: true});
      return () => { stopped = true; clearTimeout(warmTimer); controls.forEach(control => control.destroy()); };
    }

    function initFixedBottomKeyboardFix() {
      const hidden = new Map();
      let restoreTimer;
      const isField = element => root.contains(element) && element?.matches('input, textarea, select');
      const restore = () => {
        clearTimeout(restoreTimer);
        hidden.forEach(({ value, priority }, element) => {
          if (value) element.style.setProperty('display', value, priority);
          else element.style.removeProperty('display');
        });
        hidden.clear();
      };
      on(root, 'focusin', event => {
        if (!isField(event.target)) return;
        clearTimeout(restoreTimer);
        document.querySelectorAll('.m-mobile-sticky-bar, .m-button--scroll-top').forEach(element => {
          if (!hidden.has(element)) hidden.set(element, {
            value: element.style.getPropertyValue('display'),
            priority: element.style.getPropertyPriority('display'),
          });
          element.style.setProperty('display', 'none', 'important');
        });
      });
      on(root, 'focusout', event => {
        if (!isField(event.target)) return;
        clearTimeout(restoreTimer);
        restoreTimer = setTimeout(() => {
          if (!isField(document.activeElement)) restore();
        }, 200);
      });
      abort.signal.addEventListener('abort', restore, { once: true });
      return restore;
    }

    function initMobileInputs(form) {
      const vv = window.visualViewport;
      let pending, timer;
      const reveal = () => {
        const input = pending;
        if (!input || document.activeElement !== input) return;
        const rect = input.getBoundingClientRect();
        const top = vv?.offsetTop || 0;
        if (rect.top < top + 16 || rect.bottom > top + (vv?.height || window.innerHeight) - 16) input.scrollIntoView?.({block: 'center', behavior: 'smooth'});
      };
      on(form, 'focusin', event => {
        if (!event.target.matches('input, textarea, select')) return;
        pending = event.target;
        clearTimeout(timer);
        timer = setTimeout(reveal, 300);
      });
      on(form, 'focusout', () => { pending = null; clearTimeout(timer); });
      on(vv, 'resize', reveal);
      abort.signal.addEventListener('abort', () => clearTimeout(timer), {once: true});
      const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (!ios) return;
      form.querySelectorAll('.custom-input, label[for]').forEach(element => {
        let start, moved;
        element.style.pointerEvents = 'auto';
        if (element.matches('input')) element.style.webkitUserSelect = 'text';
        element.addEventListener('touchstart', event => {
          start = {x: event.touches[0].clientX, y: event.touches[0].clientY, time: Date.now()}; moved = false;
        }, {passive: true, signal: abort.signal});
        element.addEventListener('touchmove', event => {
          if (start && Math.hypot(event.touches[0].clientX - start.x, event.touches[0].clientY - start.y) > 10) moved = true;
        }, {passive: true, signal: abort.signal});
        element.addEventListener('touchend', event => {
          if (!start || moved || Date.now() - start.time >= 300) return;
          const input = element.matches('input') ? element : form.querySelector('#' + CSS.escape(element.htmlFor));
          if (!input || input.disabled || (element.matches('label') && input.type !== 'radio')) return;
          event.preventDefault();
          input.focus();
          input.click();
        }, {passive: false, signal: abort.signal});
      });
    }

    function initForm() {
      const form = root.querySelector('#custom-checkout-form');
      if (!form) return;
      on(form, 'submit', event => { event.preventDefault(); void submitOrder(form); });
      on(form, 'change', event => {
        if (event.target.closest('[data-control="delivery"]')) toggleDelivery(form);
        if (event.target.closest('[data-control="payment"]')) togglePayment(form);
      });
      on(form, 'focusout', event => {
        const input = event.target;
        if (!input.matches('input:not([type="radio"])') || input.disabled) return;
        if (/^customer-(name|surname|patronymic)$/.test(input.id)) {
          input.value = input.value.trim().replace(/^./u, letter => letter.toUpperCase());
        }
        validateInput(input);
      });
      on(form, 'input', event => {
        if (event.target.matches('.custom-input')) validateInput(event.target, false);
      });
      toggleDelivery(form);
      togglePayment(form);
      destroyAutocomplete();
      destroyAutocomplete = initNovaPoshta(form);
      const phone = form.querySelector('#customer-phone');
      if (phone) {
        phone.inputMode = 'tel';
        if (typeof window.IMask === 'function') {
          const mask = window.IMask(phone, {
            mask: '+{38\\0} (P0) 000-00-00',
            definitions: {P: /[1-9]/},
            lazy: true,
            placeholderChar: '_',
            overwrite: true,
          });
          abort.signal.addEventListener('abort', () => mask.destroy(), { once: true });
        }
        on(phone, 'focus', () => {
          setTimeout(() => {
            if (document.activeElement === phone) phone.setSelectionRange(phone.value.length, phone.value.length);
          }, 1);
        });
      }
      initMobileInputs(form);
      initOrderButton(form);
    }

    function validateInput(input, show = true, required = false) {
      const field = input.closest('.custom-form-field');
      const value = input.value.trim();
      let message = '';
      if (/^customer-(name|surname|patronymic)$/.test(input.id)) {
        if (value && !/^[а-яА-ЯіІїЇєЄґҐa-zA-Z'’-]{2,}$/.test(value)) message = 'Мінімум 2 букви, без цифр і символів';
        input.setCustomValidity(message);
      } else if (input.id === 'customer-phone') {
        if (value && !/^380[1-9]\d{8}$/.test(value.replace(/\D/g, ''))) message = 'Введіть повний номер: після +380 код оператора без початкового 0';
        input.setCustomValidity(message);
      } else if (input.type === 'email') {
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) message = 'Введіть коректний email';
        else if (value && /(^\.|\.@|\.\.)/.test(value)) message = 'Перевірте email: крапка не може бути на початку, перед @ або двічі поспіль';
        input.setCustomValidity(message);
      }
      if (required && input.required && !value) message = 'Заповніть це поле';
      const valid = input.checkValidity();
      const error = show && !valid && (!!value || required);
      const success = show && !!value && valid;
      field?.classList.toggle('has-error', error);
      field?.classList.toggle('has-success', success);
      input.classList.toggle('input-error', error);
      input.classList.toggle('input-success', success);
      input.setAttribute('aria-invalid', String(error));
      field?.querySelector('.error-message')?.remove();
      if (error && field) {
        const node = document.createElement('div');
        node.className = 'error-message';
        node.setAttribute('aria-live', 'polite');
        node.textContent = message || input.validationMessage;
        field.append(node);
      }
      return valid;
    }

    function orderNotice(form, message) {
      const node = form.querySelector('#sensme-cart-availability');
      if (node) node.textContent = message;
    }

    function persistAttempt(value) {
      // Save before network I/O so a lost response or reload reuses the same request.
      if (value) sessionStorage.setItem(attemptKey, JSON.stringify(value));
      else sessionStorage.removeItem(attemptKey);
      attempt = value;
    }

    function freezeOrder(form, frozen) {
      form.querySelectorAll('input, textarea, select').forEach(input => {
        if (frozen) {
          if (!input.hasAttribute('data-order-disabled')) input.dataset.orderDisabled = String(input.disabled);
          input.disabled = true;
        } else if (input.hasAttribute('data-order-disabled')) {
          input.disabled = input.dataset.orderDisabled === 'true';
          delete input.dataset.orderDisabled;
        }
      });
      setBusy(frozen);
    }

    async function initOrderButton(form) {
      clearTimeout(healthTimer);
      if (abort.signal.aborted) return;
      const button = form.querySelector('.custom-checkout-submit');
      button.type = 'submit';
      button.disabled = true;
      if (!storageAvailable) {
        orderNotice(form, 'Дозвольте зберігання даних для цього сайту та оновіть сторінку.');
        return;
      }
      if (attempt?.receipt) {
        freezeOrder(form, true);
        showReceipt(attempt.receipt);
        await finishReceipt(form);
        return;
      }
      if (attempt?.payload) {
        freezeOrder(form, true);
        button.querySelector('.btn-text').textContent = 'Перевірити замовлення';
        orderNotice(form, 'Збережено попередню спробу. Перевірте її стан, щоб не дублювати замовлення.');
      }
      try {
        const {response, data: health} = await fetchJSON(`${ordersApi}/health`, {}, 5000);
        if (!response.ok || !health.ready) throw new Error('unavailable');
        button.dataset.ready = 'true';
        button.disabled = orderBusy || (busy && !attempt?.payload);
        if (!attempt) orderNotice(form, 'Після оформлення менеджер зв’яжеться з вами для підтвердження та оплати.');
        else if (attempt.payload) scheduleOrderCheck(form);
      } catch {
        if (abort.signal.aborted) return;
        orderNotice(form, 'Відновлюємо зв’язок із сервісом замовлень. Залишайтеся на сторінці, зачекайте кілька секунд.');
        healthTimer = setTimeout(() => void initOrderButton(form), 5000);
      }
    }

    function scheduleOrderCheck(form) {
      clearTimeout(retryTimer);
      if (abort.signal.aborted || !attempt?.payload || automaticChecks >= 8) return;
      const delay = Math.min(5000 * ++automaticChecks, 15000);
      retryTimer = setTimeout(() => void submitOrder(form, true), delay);
    }

    function readAttribution() {
      try { return JSON.parse(localStorage.getItem('sensme-attribution-v1') || 'null'); }
      catch { return null; }
    }

    function cartSignature(cart) {
      return JSON.stringify(cart.items.map(i => ({key: i.key, quantity: i.quantity})).sort((a, b) => a.key.localeCompare(b.key)));
    }

    function showReceipt(receipt) {
      sessionStorage.setItem('sensme-order-receipt-v1', JSON.stringify({...receipt, createdAt: receipt.createdAt || Date.now()}));
      let node = root.querySelector('[data-order-receipt]');
      if (!node) {
        node = document.createElement('p');
        node.dataset.orderReceipt = '';
        node.className = 'custom-checkout-notice';
        node.setAttribute('role', 'status');
        node.tabIndex = -1;
        root.prepend(node);
      }
      node.textContent = `Дякуємо! Замовлення ${receipt.order_name} прийнято. Менеджер зв’яжеться з вами для підтвердження та оплати.`;
      node.focus();
    }

    async function finishReceipt(form) {
      const destination = new URL(`${shopRoot}cart`, window.location.origin);
      destination.searchParams.set('view', 'thank-you');
      const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewTheme) destination.searchParams.set('preview_theme_id', previewTheme);
      window.location.assign(destination.href);
      // The destination owns cleanup; navigation must not interrupt a cart mutation.
    }

    async function submitOrder(form, automatic = false) {
      const button = form.querySelector('.custom-checkout-submit');
      if (abort.signal.aborted || orderBusy || attempt?.receipt || button.dataset.ready !== 'true' || (busy && !attempt?.payload)) return;
      clearTimeout(retryTimer);
      if (!automatic) automaticChecks = 0;
      if (!attempt?.payload) {
        const inputs = [...form.querySelectorAll('input:not(:disabled):not([type="radio"])')];
        const invalid = inputs.filter(input => !validateInput(input, true, true));
        if (invalid.length) {
          invalid[0].focus();
          invalid[0].scrollIntoView?.({ block: 'center', behavior: 'smooth' });
          return;
        }
      }
      orderBusy = true;
      button.disabled = true;
      button.querySelector('.btn-text').textContent = 'Обробляємо…';
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      const progressTimer = setTimeout(() => {
        button.querySelector('.btn-text').textContent = 'Перевіряємо замовлення…';
        orderNotice(form, 'Очікуємо підтвердження замовлення. Повторно заповнювати форму не потрібно.');
      }, 8000);
      let sent = !!attempt?.payload;
      let retry = false, invalidField;
      try {
        if (!attempt?.payload) {
          const value = selector => form.querySelector(selector)?.value.trim() || '';
          const type = form.querySelector('[data-control="delivery"] input:checked').id.replace('delivery-', '');
          const fields = form.querySelector(`[data-delivery="${type}"]`);
          const inputs = [...fields.querySelectorAll('input')];
          const [city, destination] = inputs;
          if ((!city.dataset.npRef && city.dataset.npFallback !== '1') || (type !== 'courier' && !destination.dataset.npRef && destination.dataset.npFallback !== '1')) throw new Error('Оберіть місто та відділення або поштомат зі списку.');
          let digits = value('#customer-phone').replace(/\D/g, '');
          if (digits.length === 10) digits = `38${digits}`;
          const customer = {firstName: value('#customer-name'), lastName: value('#customer-surname'), patronymic: type === 'courier' ? value('#customer-patronymic') : '', phone: `+${digits}`, email: value('#customer-email')};
          const payment = form.querySelector('[data-control="payment"] input:checked').id.replace('payment-', '');
          const delivery = {type, city: city.value.trim(), address: type === 'courier' ? inputs.slice(1).map(i => i.value.trim()).filter(Boolean).join(', ') : destination.value.trim()};
          delivery.cityRef = city.dataset.npRef || '';
          delivery.destinationRef = destination.dataset.npRef || '';
          delivery.manual = city.dataset.npFallback === '1' || destination.dataset.npFallback === '1' || (type === 'courier' && !destination.dataset.npRef);
          if (type === 'courier') {
            delivery.street = destination.value.trim();
            delivery.building = inputs[2]?.value.trim() || '';
            delivery.apartment = inputs[3]?.value.trim() || '';
          }
          freezeOrder(form, true);
          const {response, data: cart} = await fetchJSON(`${shopRoot}cart.js`);
          if (!response.ok) throw new Error(messages.cartError);
          if (!cart.items.length) throw new Error('Кошик порожній.');
          if (cart.items.some(i => i.selling_plan_allocation || Object.values(i.properties || {}).some(Boolean))) throw new Error('Для цього товару потрібне оформлення через менеджера.');
          const codes = (cart.discount_codes || []).filter(c => c.applicable);
          if (codes.length > 1) throw new Error('Залиште один промокод або зверніться до менеджера.');
          persistAttempt({cartSignature: cartSignature(cart), payload: {
            requestId: crypto.randomUUID(), customer, delivery, payment,
            attribution: readAttribution(),
            items: cart.items.map(i => ({variantId: String(i.variant_id), quantity: i.quantity})),
            expectedTotalCents: cart.total_price, currency: cart.currency,
            discountCode: codes[0]?.code || '',
          }});
        }
        freezeOrder(form, true);
        sent = true;
        const {response, data: result} = await fetchJSON(`${ordersApi}/orders`, {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(attempt.payload),
        }, 30000);
        if (response.ok && result.success === true) {
          const submitted = attempt.cartSignature;
          persistAttempt({receipt: {order_name: result.order_name, name: attempt.payload.customer.firstName, createdAt: Date.now()}, cartSignature: submitted});
          showReceipt(attempt.receipt);
          button.querySelector('.btn-text').textContent = 'Замовлення прийнято';
          orderNotice(form, 'Замовлення збережено. Оплату ще не отримано.');
          await finishReceipt(form);
          return;
        }
        if (response.status === 400 || (response.status === 409 && ['price_changed', 'unavailable', 'rejected'].includes(result.code))) {
          persistAttempt(null);
          sent = false;
          invalidField = result.field || ({invalid_email: 'email', invalid_phone: 'phone'})[result.code];
          throw new Error(result.error || 'Перевірте дані та оновіть кошик.');
        }
        retry = (response.status === 202 && ['queued', 'creating', 'draft'].includes(result.code)) || response.status === 503 || response.status === 429;
        orderNotice(form, result.code === 'needs_review'
          ? `Перевіряємо замовлення. Зверніться до менеджера та вкажіть номер ${attempt.payload.requestId}. Не створюйте нове замовлення.`
          : result.code === 'idempotency_conflict' ? 'Не вдалося підтвердити дані збереженої спроби. Зверніться до менеджера.'
          : automaticChecks < 8 ? 'Замовлення ще перевіряється. Результат з’явиться автоматично.'
          : 'Перевірка триває довше, ніж зазвичай. Натисніть «Перевірити замовлення» або зверніться до менеджера.');
      } catch (error) {
        retry = sent && !!attempt?.payload;
        orderNotice(form, sent
          ? automaticChecks < 8 ? 'Зв’язок перервався. Автоматично перевіряємо замовлення: повторної покупки не буде.' : 'Зв’язок перервався. Натисніть «Перевірити замовлення»: повторної покупки не буде.'
          : error.name === 'AbortError' ? 'Не вдалося перевірити кошик. Перевірте з’єднання та спробуйте ще раз.'
          : error.message || 'Не вдалося оформити замовлення. Спробуйте ще раз.');
      } finally {
        clearTimeout(progressTimer);
        orderBusy = false;
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        if (!attempt) freezeOrder(form, false);
        if (!attempt && ['email', 'phone'].includes(invalidField)) {
          const field = form.querySelector(`#customer-${invalidField}`);
          field?.setAttribute('aria-invalid', 'true');
          field?.closest('.custom-form-field')?.classList.add('has-error');
          field?.focus();
        }
        button.disabled = !!attempt?.receipt;
        if (!attempt?.receipt) button.querySelector('.btn-text').textContent = attempt?.payload ? 'Перевірити замовлення' : 'Оформити замовлення';
        if (retry) scheduleOrderCheck(form);
      }
    }

    on(root, 'click', event => {
      const quantity = event.target.closest('.custom-qty-btn, .custom-cart-item-remove');
      if (quantity) return void changeQuantity(quantity);
      if (event.target.closest('#promo-toggle')) togglePromo();
      if (event.target.closest('#promo-apply-btn')) void applyPromo();
    });
    on(root, 'keydown', event => {
      if (event.key === 'Enter' && event.target.matches('#promo-code-input')) {
        event.preventDefault();
        void applyPromo();
      }
    });
    restoreBottomUi = initFixedBottomKeyboardFix();
    initForm();
    fetch(`${shopRoot}cart.js`, { signal: abort.signal })
      .then(response => { if (!response.ok) throw new Error(messages.cartError); return response.json(); })
      .then(syncPromo)
      .catch(() => {});
  }

  const initialize = () => document.querySelectorAll('[data-sensme-cart]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
